import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useModalStore from "@/store/useModalStore";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export const RESEND_ACCESS_CONFIRMATION_MODAL = "resendAccessConfirmationModal";

interface ResendAccessConfirmationModalProps {
  onConfirm: () => void;
  isSending: boolean;
  customerEmail?: string;
  onEditEmail?: () => void;
  canUpdateEmail?: boolean;
}

export default function ResendAccessConfirmationModal({
  onConfirm,
  isSending,
  customerEmail,
  onEditEmail,
  canUpdateEmail,
}: ResendAccessConfirmationModalProps) {
  const { t } = useTranslation(["sales", "common"]);
  const { modals, closeModal } = useModalStore();

  const isOpen = modals[RESEND_ACCESS_CONFIRMATION_MODAL] || false;

  const handleClose = () => {
    if (isSending) return;
    closeModal(RESEND_ACCESS_CONFIRMATION_MODAL);
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-colorBorder">
        <DialogHeader className="p-6 pb-4 border-b border-colorBorder">
          <DialogTitle className="text-xl font-semibold">
            {t("sales:resend_access_confirmation_title")}
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 pt-2 space-y-4">
          <Alert
            variant="warning"
            className="bg-yellow-500/10 border-yellow-500/20 text-yellow-500"
          >
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <AlertTitle>{t("common:attention")}</AlertTitle>
            <AlertDescription>
              {t("sales:resend_access_warning_once")}
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("sales:resend_access_confirmation_description")}
            </p>

            {customerEmail && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {t("common:send_to")}
                </label>
                <div className="flex gap-2">
                  <div className="flex h-10 flex-1 items-center rounded-md border border-input bg-[#09090b] px-3 py-2 text-sm text-foreground opacity-50 cursor-not-allowed">
                    {customerEmail}
                  </div>
                  {canUpdateEmail && onEditEmail && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onEditEmail}
                      disabled={isSending}
                    >
                      {t("common:edit")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="p-6 border-t gap-3 border-colorBorder">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSending}
          >
            {t("common:cancel")}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isSending}>
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("common:sending")}
              </>
            ) : (
              <>{t("sales:confirm_resend")}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
